import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Shield, Plus, Pencil, Trash2, Search, MessageCircle, TrendingUp, Zap } from 'lucide-react';

const CATEGORY_LABELS = {
  price: 'Price Objection',
  trust: 'Trust / Credibility',
  timing: 'Timing / Not Now',
  competitor: 'Competitor Comparison',
  knowledge_gap: 'Knowledge Gap',
  fear_risk: 'Fear / Risk',
  commitment: 'Commitment / Decision',
  authority: 'Authority / Not Decided',
  need: 'Need / Relevance',
  urgency: 'Urgency',
  other: 'Other',
};

const CATEGORY_COLORS = {
  price: 'bg-red-500/10 text-red-600',
  trust: 'bg-blue-500/10 text-blue-600',
  timing: 'bg-yellow-500/10 text-yellow-600',
  competitor: 'bg-purple-500/10 text-purple-600',
  knowledge_gap: 'bg-orange-500/10 text-orange-600',
  fear_risk: 'bg-pink-500/10 text-pink-600',
  commitment: 'bg-teal-500/10 text-teal-600',
  authority: 'bg-indigo-500/10 text-indigo-600',
  need: 'bg-emerald-500/10 text-emerald-600',
  urgency: 'bg-amber-500/10 text-amber-600',
  other: 'bg-muted text-foreground',
};

const EMPTY_FORM = { category: 'price', customer_says: '', recommended_response: '', tips: '', effectiveness_score: 0 };

export default function ObjectionLibraryPage() {
  const [objections, setObjections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await apiClient.get('/claret/objections');
      setObjections(res.data?.objections || []);
      setCategories(res.data?.categories || Object.keys(CATEGORY_LABELS));
    } catch {
      toast.error('Failed to load objections');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  }

  function openEdit(obj) {
    setEditing(obj);
    setForm({
      category: obj.category || 'other',
      customer_says: obj.customer_says || '',
      recommended_response: obj.recommended_response || '',
      tips: obj.tips || '',
      effectiveness_score: obj.effectiveness_score || 0,
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.customer_says.trim()) { toast.error('Customer objection is required'); return; }
    if (!form.recommended_response.trim()) { toast.error('Recommended response is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/claret/objections/${editing.id}`, form);
        toast.success('Objection updated');
      } else {
        await apiClient.post('/claret/objections', form);
        toast.success('Objection added to library');
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this objection script?')) return;
    try {
      await apiClient.delete(`/claret/objections/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  }

  const filtered = objections.filter(o => {
    const matchSearch = !search || (o.customer_says || '').toLowerCase().includes(search.toLowerCase()) || (o.recommended_response || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || o.category === catFilter;
    return matchSearch && matchCat;
  });

  const catCounts = {};
  objections.forEach(o => { catCounts[o.category] = (catCounts[o.category] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="objection-library-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Objection Handling Library</h1>
            <p className="text-muted-foreground text-sm">Sales scripts that Claret uses to coach agents on handling customer objections</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="add-objection-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Objection Script
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <MessageCircle className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <p className="text-3xl font-bold">{objections.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Scripts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <Zap className="h-6 w-6 mx-auto mb-2 text-red-500" />
            <p className="text-3xl font-bold">{catCounts['price'] || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Price Objections</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <Shield className="h-6 w-6 mx-auto mb-2 text-blue-500" />
            <p className="text-3xl font-bold">{catCounts['trust'] || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Trust Objections</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-3xl font-bold">{objections.reduce((s, o) => s + (o.usage_count || 0), 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Times Used</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search objections..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="objection-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[200px]" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories ({objections.length})</SelectItem>
            {categories.map(c => (
              <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c} ({catCounts[c] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline">{filtered.length} script(s)</Badge>
      </div>

      {/* Objection Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Objection Scripts</h3>
            <p className="text-muted-foreground text-sm mb-4">Add your first objection handling script for Claret to use.</p>
            <Button onClick={openCreate}>Add Script</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((obj, i) => (
            <Card key={obj.id} className="group hover:border-primary/30 transition-colors" data-testid={`objection-card-${i}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={CATEGORY_COLORS[obj.category] || CATEGORY_COLORS.other}>
                        {CATEGORY_LABELS[obj.category] || obj.category}
                      </Badge>
                      {obj.usage_count > 0 && (
                        <Badge variant="outline" className="text-xs">{obj.usage_count}x used</Badge>
                      )}
                    </div>
                    <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3">
                      <p className="text-xs font-medium text-red-500 mb-1">Customer says:</p>
                      <p className="text-sm italic">"{obj.customer_says}"</p>
                    </div>
                    <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3">
                      <p className="text-xs font-medium text-green-600 mb-1">Recommended response:</p>
                      <p className="text-sm whitespace-pre-wrap">{obj.recommended_response}</p>
                    </div>
                    {obj.tips && (
                      <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-600 mb-1">Tips:</p>
                        <p className="text-sm">{obj.tips}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(obj)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDelete(obj.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="objection-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Objection Script</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="obj-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer Says (the objection)</Label>
              <Textarea
                value={form.customer_says}
                onChange={e => setForm(f => ({ ...f, customer_says: e.target.value }))}
                placeholder='e.g. "This is too expensive, I found cheaper options online"'
                className="min-h-[80px]"
                data-testid="obj-customer-says"
              />
            </div>
            <div>
              <Label>Recommended Response</Label>
              <Textarea
                value={form.recommended_response}
                onChange={e => setForm(f => ({ ...f, recommended_response: e.target.value }))}
                placeholder="The proven response that works best for this objection..."
                className="min-h-[120px]"
                data-testid="obj-response"
              />
            </div>
            <div>
              <Label>Tips for Agent (optional)</Label>
              <Textarea
                value={form.tips}
                onChange={e => setForm(f => ({ ...f, tips: e.target.value }))}
                placeholder="e.g. Pause after stating the value. Let them process. Don't rush to discount."
                className="min-h-[60px]"
                data-testid="obj-tips"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-objection-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Add to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
